"use client";

import type { GetFlyOutRouteReturn } from "../app/storyblok/[localeString]/energydrink/api/fly-out/route";
import { RailFlyOverModal } from "../components/RailFlyOver/RailFlyOverModal";
import { FlyOutData } from "./data/getFlyOutData";
import { Locale } from "./locales.types";
import { Route } from "next";
import { createContext, useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";

const RailFlyOverContext = createContext<{
  localeString: string;
  show: () => void;
  hide: () => void;
  isLoading: boolean;
  matchingLocale?: Locale;
}>({
  localeString: "",
  show: () => {},
  hide: () => {},
  isLoading: false,
  matchingLocale: undefined,
});

export const RailFlyOverContextProvider = ({
  matchingLocale,
  children,
  initialFlyOutData,
}: {
  matchingLocale?: Locale;
  children: React.ReactNode;
  /**
   * Pre-supplied fly-out data. When set, the endpoint isn't fetched at all --
   * used by Storybook, which has no Next server to serve it.
   */
  initialFlyOutData?: FlyOutData;
}) => {
  // TODO: potentially refactor to "visible" | "loading" | "mounted" or similar
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [flyOutData, setFlyOutData] = useState<FlyOutData | null>(
    initialFlyOutData ?? null,
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!matchingLocale || initialFlyOutData) {
      return;
    }

    const controller = new AbortController();

    setIsLoading(true);

    fetch(
      `/${matchingLocale.localeString}/energydrink/api/fly-out` satisfies Route,
      { signal: controller.signal },
    )
      .then((response) => response.json() as Promise<GetFlyOutRouteReturn>)
      .then(({ data }) => {
        if (data) {
          setFlyOutData(data);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        // The request was superseded (locale change/unmount), not a failure.
        if (controller.signal.aborted) {
          return;
        }
        console.error("Error fetching flyout data:", error);
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [matchingLocale, initialFlyOutData]);

  useEffect(() => {
    if (isVisible) {
      document.body.setAttribute("data-railflyover-open", "true");
    } else {
      document.body.removeAttribute("data-railflyover-open");
    }
  }, [isVisible]);

  return (
    <RailFlyOverContext.Provider
      value={{
        localeString: matchingLocale?.localeString ?? "",
        show: () => setIsVisible(true),
        hide: () => setIsVisible(false),
        isLoading,
        matchingLocale,
      }}
    >
      {children}
      {isMounted &&
        !isLoading &&
        !!flyOutData &&
        createPortal(
          <RailFlyOverModal
            title={flyOutData.title}
            featuredProducts={flyOutData.featuredProducts}
            categories={flyOutData.categories}
            onClose={() => setIsVisible(false)}
            isOpen={isVisible}
          />,
          document.body,
        )}
    </RailFlyOverContext.Provider>
  );
};

export const useRailFlyOverContext = () => {
  return useContext(RailFlyOverContext);
};
